/**
 * FpnBadge — renders the (FPN) Fridge.com Part Number on a part page.
 *
 * The part-side twin of <FinBadge> (FRIDGE-333, owner ruling 08/16/2026: every
 * part gets an FPN and never a FIN). Same monospace chip under the H1, same
 * quiet weight, same click-through to the identifier explainer — /about-us/fpn
 * carries the FPN section (#fpn) so a first-time visitor learns what it is.
 *
 * Display contract:
 *   - Monospace, uppercase with hyphen ("FPN-0000012") — the FIN's shape, so
 *     the two read as siblings and never as the same identifier
 *   - Renders ONLY a well-formed value from products.fpn (minted in the
 *     database by mint_fpn(); never invented here). Null → nothing, which is
 *     what every part shows until 20260816221500_fpn_part_numbers.sql is
 *     applied — Real-or-Hidden, never a placeholder.
 *   - Never rendered on an appliance page: the PDP renders <FinBadge> for its
 *     family and this for parts, decided by lib/parts/fpn.ts, one switch.
 */

import Link from 'next/link'
import { isValidFpn } from '@/lib/parts/fpn'

interface FpnBadgeProps {
  fpn: string | null | undefined
  className?: string
}

export function FpnBadge({ fpn, className = '' }: FpnBadgeProps) {
  if (!isValidFpn(fpn)) return null
  return (
    <Link
      href="/about-us/fpn"
      className={`inline-flex items-center gap-1.5 text-xs font-mono text-gray-600 hover:text-[#0469f0] transition-colors ${className}`}
      aria-label={`Fridge.com Part Number ${fpn} — learn more about FPNs`}
      title="Fridge.com Part Number (FPN) — click to learn more"
      data-testid="fpn-badge"
    >
      <span className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200 group-hover:border-[#0469f0]/40 tracking-wider">
        {fpn}
      </span>
    </Link>
  )
}

export default FpnBadge
